import { useState, useEffect } from "react";
import axios from "axios";
import ProspectForm from "./ProspectForm";
import AlertDialog from "./AlertDialog";
import Select from "react-select";
import ProspectsCalendar from "./ProspectsCalendar";
import ProspectsAnalytics from "./ProspectsAnalytics";
import { TrashIcon } from "@heroicons/react/24/outline";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const formatDate = (date) =>
  date ? new Date(date).toISOString().split("T")[0] : "N/A";

const STAGE_OPTIONS = [
  { value: "Prospecting", label: "Prospecting" },
  { value: "Lost", label: "Lost" },
  { value: "Won", label: "Won" },
];

const SOURCE_OPTIONS = [
  { value: "Rocketreach", label: "Rocketreach" },
  { value: "Urban Toronto", label: "Urban Toronto" },
  { value: "Referral", label: "Referral" },
  { value: "Other", label: "Other" },
];

export default function ProspectsList({
  prospects: initialProspects,
  setProspects,
  sortOrder,
  handleSort,
  navigate,
  users,
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    onConfirm: () => {},
    showCancel: false,
  });
  const [selectedStages, setSelectedStages] = useState(() => {
    const savedStages = sessionStorage.getItem("prospectsFilter");
    return savedStages ? JSON.parse(savedStages) : ["Prospecting"];
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState("list");
  const [sortField, setSortField] = useState("createdAt");
  const [titleSortOrder, setTitleSortOrder] = useState("asc");
  const [visibleEventTypes, setVisibleEventTypes] = useState({
    activity: true,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [prospects, setLocalProspects] = useState([]);
  const itemsPerPage = 50;

  useEffect(() => {
    sessionStorage.setItem("prospectsFilter", JSON.stringify(selectedStages));
  }, [selectedStages]);

  const fetchProspects = async (page = 1) => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }
      const stageQuery =
        selectedStages.length > 0 ? `stage=${selectedStages.join(",")}` : "";
      const response = await axios.get(
        `https://erp-production-72da01c8e651.herokuapp.com/api/prospects?page=${page}&limit=${itemsPerPage}${
          stageQuery ? `&${stageQuery}` : ""
        }`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const fetchedProspects = Array.isArray(response.data.prospects)
        ? response.data.prospects
        : [];
      setLocalProspects(fetchedProspects);
      setProspects(fetchedProspects);
      setTotalItems(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
      setCurrentPage(response.data.currentPage || page);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to fetch prospects. Please try again.";
      setError(errorMessage);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: errorMessage,
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProspects(currentPage);
  }, [selectedStages, currentPage]);

  const handleDeleteConfirm = (prospectId) => {
    setAlert({
      open: true,
      type: "warning",
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this prospect? This action cannot be undone.",
      onConfirm: () => handleDelete(prospectId),
      showCancel: true,
    });
  };

  const handleDelete = async (prospectId) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }
      await axios.delete(
        `https://erp-production-72da01c8e651.herokuapp.com/api/prospects/${prospectId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setLocalProspects((prev) => prev.filter((p) => p._id !== prospectId));
      setProspects((prev) => prev.filter((p) => p._id !== prospectId));
      setTotalItems((prev) => prev - 1);
      if (prospects.length === 1 && currentPage > 1) {
        setCurrentPage((prev) => prev - 1);
      } else {
        fetchProspects(currentPage);
      }
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "Prospect deleted successfully!",
        onConfirm: () => {
          setAlert({ ...alert, open: false });
          navigate("/Prospects");
        },
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to delete prospect: " + err.message;
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: errorMessage,
        onConfirm: () => setAlert({ ...alert, open: false }),
      });
    }
  };

  const handleStageFilterChange = (selected) => {
    setSelectedStages(selected ? selected.map((option) => option.value) : []);
    setCurrentPage(1);
  };

  const handleTitleSort = () => {
    const newSortOrder = titleSortOrder === "asc" ? "desc" : "asc";
    setTitleSortOrder(newSortOrder);
    setSortField("projectName");
  };

  const sortedProspects = [...prospects].sort((a, b) => {
    if (sortField === "projectName") {
      return titleSortOrder === "asc"
        ? a.projectName.localeCompare(b.projectName)
        : b.projectName.localeCompare(a.projectName);
    }
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
  });

  const toggleEventType = (eventType) => {
    setVisibleEventTypes((prev) => ({
      ...prev,
      [eventType]: !prev[eventType],
    }));
  };

  const handleRowClick = (prospectId, event) => {
    if (event.target.closest(".actions-column")) {
      return;
    }
    navigate(`/Prospects/${prospectId}`);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className="max-w-full mx-auto">
      <div className="bg-gray-300 shadow-xl rounded-xl p-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-white bg-gray-700 rounded-md px-4 py-2">
            Prospects
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setIsCreating(true)}
              className="bg-green-600 text-white font-semibold px-4 py-2 rounded-md hover:bg-green-500"
            >
              + Create New Prospect
            </button>
            <button
              onClick={() => setView("list")}
              className={classNames(
                view === "list"
                  ? "bg-orange-400 text-white font-semibold"
                  : "bg-gray-600 text-gray-200 hover:bg-gray-500 font-semibold",
                "px-4 py-2 rounded-md",
              )}
            >
              List View
            </button>
            <button
              onClick={() => setView("calendar")}
              className={classNames(
                view === "calendar"
                  ? "bg-orange-400 text-white font-semibold"
                  : "bg-gray-600 text-gray-200 hover:bg-gray-500 font-semibold",
                "px-4 py-2 rounded-md",
              )}
            >
              Calendar View
            </button>
            <button
              onClick={() => setView("analytics")}
              className={classNames(
                view === "analytics"
                  ? "bg-orange-400 text-white font-semibold"
                  : "bg-gray-600 text-gray-200 hover:bg-gray-500 font-semibold",
                "px-4 py-2 rounded-md",
              )}
            >
              Analytics View
            </button>
          </div>
        </div>

        {view === "list" && (
          <>
            {isCreating && (
              <div className="mb-6 bg-gray-700 p-6 rounded-lg shadow-lg">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-orange-400">
                    Create New Prospect
                  </h3>
                  <button
                    onClick={() => setIsCreating(false)}
                    className="text-gray-400 hover:text-gray-200 text-xl"
                  >
                    ✕
                  </button>
                </div>
                <ProspectForm
                  setProspects={setProspects}
                  setIsCreating={setIsCreating}
                  users={users}
                  navigate={navigate}
                />
              </div>
            )}

            <div className="mb-6 p-4">
              <label className="block text-lg font-semibold text-gray-800 mb-3">
                Filter by Stage
              </label>
              <Select
                isMulti
                options={STAGE_OPTIONS}
                value={STAGE_OPTIONS.filter((option) =>
                  selectedStages.includes(option.value),
                )}
                onChange={handleStageFilterChange}
                placeholder="Select one or more stages..."
                className="mt-1 block max-w-xs rounded-md bg-gray-700 border-gray-600 text-gray-200 focus:border-orange-500 focus:ring-orange-500 sm:text-sm font-semibold"
                isClearable={true}
                isSearchable={false}
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    backgroundColor: "bg-gray-500",
                    borderColor: state.isFocused ? "#f97316" : "#4b5563",
                    boxShadow: state.isFocused ? "0 0 0 2px #f97316" : "none",
                    "&:hover": { borderColor: "#f97316" },
                  }),
                  input: (provided) => ({
                    ...provided,
                    color: "#ffffff",
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: "#ffffff",
                  }),
                  multiValue: (provided) => ({
                    ...provided,
                    backgroundColor: "#4b5563",
                  }),
                  multiValueLabel: (provided) => ({
                    ...provided,
                    color: "#ffffff",
                  }),
                  multiValueRemove: (provided) => ({
                    ...provided,
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#f97316",
                      color: "#ffffff",
                    },
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: "#9ca3af",
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? "#4b5563" : "#374151",
                    color: state.isSelected ? "#f97316" : "#ffffff",
                    "&:hover": { backgroundColor: "#4b5563" },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: "#374151",
                    borderRadius: "0.375rem",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }),
                  clearIndicator: (provided) => ({
                    ...provided,
                    color: "#ffffff",
                    "&:hover": {
                      color: "#f97316",
                    },
                  }),
                }}
              />
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-6 shadow-md">
                <div className="flex items-center">
                  <svg
                    className="h-5 w-5 text-red-600 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-red-800 font-semibold">{error}</p>
                </div>
              </div>
            )}

            {loading && (
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 text-center shadow-md">
                <div className="inline-block">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
                </div>
                <p className="text-blue-800 font-semibold">
                  Loading prospects...
                </p>
              </div>
            )}

            {!loading && sortedProspects.length === 0 ? (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-12 text-center shadow-md">
                <div className="mb-4">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 mb-4">
                    <svg
                      className="h-8 w-8 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                      />
                    </svg>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  No Prospects Yet
                </h3>
                <p className="text-gray-600 mb-6 text-lg">
                  Get started by creating your first prospect. This is where
                  you'll track and manage potential projects.
                </p>
                <button
                  onClick={() => setIsCreating(true)}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 text-lg"
                >
                  ✨ Create Your First Prospect
                </button>
                <p className="text-gray-500 text-sm mt-6">
                  💡 Tip: Prospects help you track potential projects from
                  initial contact through conversion to leads.
                </p>
              </div>
            ) : (
              <>
                <div className="bg-gray-800 shadow-xl rounded-xl overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead className="bg-gray-700">
                      <tr>
                        <th
                          className="px-3 py-3 text-left text-sm font-semibold text-gray-200 cursor-pointer"
                          onClick={handleTitleSort}
                        >
                          Project Name{" "}
                          {sortField === "projectName" &&
                            (titleSortOrder === "desc" ? "↓" : "↑")}
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-200">
                          Company
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-200">
                          Contact
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-200">
                          Stage
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-200">
                          Lead Source
                        </th>

                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-200">
                          Company Type
                        </th>
                        <th className="px-3 py-3 text-left text-sm font-semibold text-gray-200">
                          Next Activity
                        </th>
                        <th
                          className="px-3 py-3 text-left text-sm font-semibold text-gray-200 cursor-pointer"
                          onClick={handleSort}
                        >
                          Created On{" "}
                          {sortField === "createdAt" &&
                            (sortOrder === "desc" ? "↓" : "↑")}
                        </th>
                        <th className="px-3 py-3 text-right text-sm font-semibold text-gray-200 actions-column">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {sortedProspects.map((prospect, index) => (
                        <tr
                          key={prospect._id}
                          className={classNames(
                            index % 2 === 0 ? "bg-gray-800" : "bg-gray-700",
                            "hover:bg-gray-600 transition-colors cursor-pointer",
                          )}
                          onClick={(e) => handleRowClick(prospect._id, e)}
                        >
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-300">
                            <span className="inline-flex items-center rounded-full bg-gray-200 px-2.5 py-0.5 text-xs font-medium text-black hover:bg-gray-300">
                              {prospect.projectName.slice(0, 30)}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-300">
                            {prospect.companyName.slice(0, 20)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-300">
                            {prospect.contactName || prospect.email}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs">
                            <span
                              className={classNames(
                                prospect.stage === "Won"
                                  ? "bg-green-100 text-green-700"
                                  : prospect.stage === "Lost"
                                    ? "bg-red-100 text-red-700"
                                    : "bg-yellow-100 text-yellow-700",
                                "inline-flex items-center gap-x-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
                              )}
                            >
                              <svg
                                viewBox="0 0 6 6"
                                aria-hidden="true"
                                className={classNames(
                                  prospect.stage === "Won"
                                    ? "fill-green-500"
                                    : prospect.stage === "Lost"
                                      ? "fill-red-500"
                                      : "fill-yellow-500",
                                  "size-1.5",
                                )}
                              >
                                <circle r={3} cx={3} cy={3} />
                              </svg>
                              {prospect.stage}
                            </span>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-300">
                            {prospect.sourceCategory || "—"}
                          </td>

                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-300">
                            {prospect.companyType}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-300">
                            {(() => {
                              const nextActivity = (prospect.activities || [])
                                .filter((act) => act.status === "pending")
                                .sort(
                                  (a, b) =>
                                    new Date(a.dueDate) - new Date(b.dueDate),
                                )[0];

                              if (nextActivity) {
                                return (
                                  <div className="flex flex-col">
                                    <span className="font-medium text-orange-400">
                                      {nextActivity.title.slice(0, 20)}
                                      {nextActivity.title.length > 20
                                        ? "..."
                                        : ""}
                                    </span>
                                    <span className="text-xs text-gray-400">
                                      {formatDate(nextActivity.dueDate)}
                                    </span>
                                  </div>
                                );
                              }
                              return <span className="text-gray-500">—</span>;
                            })()}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-300">
                            {formatDate(prospect.createdAt)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-xs text-right actions-column">
                            <div className="flex items-center justify-end space-x-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/Prospects/${prospect._id}`);
                                }}
                                className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-gray-900 hover:bg-blue-200"
                              >
                                View
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteConfirm(prospect._id);
                                }}
                                className="inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 hover:bg-red-200"
                              >
                                <TrashIcon className="size-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages} (Total: {totalItems})
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-500 rounded-md text-sm font-medium text-gray-200 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-500 rounded-md text-sm font-medium text-gray-200 bg-gray-600 hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {view === "calendar" && (
          <div className="bg-gray-700 px-4 py-4">
            <div className="mb-4 flex space-x-4 bg-gray-700">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={visibleEventTypes.activity}
                  onChange={() => toggleEventType("activity")}
                  className="h-4 w-4 rounded border-gray-600 text-orange-500 focus:ring-orange-500 bg-gray-700"
                />
                <span className="ml-2 text-sm text-gray-300">
                  Activities
                  <span className="ml-2 inline-block h-3 w-3 rounded-full bg-purple-300"></span>
                </span>
              </div>
            </div>
            <ProspectsCalendar
              prospects={prospects}
              users={users}
              navigate={navigate}
              visibleEventTypes={visibleEventTypes}
            />
          </div>
        )}

        {view === "analytics" && <ProspectsAnalytics />}

        <AlertDialog
          open={alert.open}
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={() => setAlert({ ...alert, open: false })}
          onConfirm={alert.onConfirm}
          showCancel={alert.showCancel}
        />
      </div>
    </div>
  );
}
